import type { TAttentionStatusSeverity } from '../model/types'

/**
 * Colour per severity level, so every cockpit paints a level the same way. The payload decides
 * which levels exist, so a level this map does not name falls back to the neutral colour rather
 * than to no colour at all.
 */
const SEVERITY_COLORS: Record<number, string> = {
  3: 'var(--color-danger-main)',
  2: 'var(--color-warning-dark)',
  1: 'var(--color-warning-main)',
}

const FALLBACK_SEVERITY_COLOR = 'var(--color-gray-500)'

/**
 * Levels that raise no alarm of their own. The lowest level is painted rather than greyed — a
 * priority a reader can name is not the same thing as no priority — but it is still the quiet one,
 * so it is listed here instead of being told apart by its colour.
 */
const NEUTRAL_SEVERITIES = new Set<number>([1])

export const getAttentionSeverityColor = (severity: TAttentionStatusSeverity): string =>
  SEVERITY_COLORS[severity] ?? FALLBACK_SEVERITY_COLOR

/** The level everything else is measured against: the one a view is allowed to shout about. */
const TOP_SEVERITY = 3

/**
 * Whether a level is the worst one there is. A view that moves — a pulsing ring, a badge over a
 * card's corner — reads its own level through this, so only the top one ever draws the eye back.
 */
export const isAttentionSeverityTop = (severity: TAttentionStatusSeverity): boolean =>
  severity === TOP_SEVERITY

/**
 * Whether a level carries no alarm of its own. A view that shouts — a pulsing hero meter, a filled
 * badge — reads its own top level through this and stays quiet where the worst thing on screen is
 * the lowest level.
 */
export const isAttentionSeverityNeutral = (severity: TAttentionStatusSeverity): boolean =>
  NEUTRAL_SEVERITIES.has(severity) ||
  getAttentionSeverityColor(severity) === FALLBACK_SEVERITY_COLOR

/**
 * Colour a row's action reads in. A quiet level would make an action a low-contrast link on white,
 * so there the action takes the plain text colour instead.
 */
export const getAttentionSeverityActionColor = (severity: TAttentionStatusSeverity): string =>
  isAttentionSeverityNeutral(severity)
    ? 'var(--color-gray-900)'
    : getAttentionSeverityColor(severity)

/** The levels a list actually carries, most severe first — the order the widget groups by. */
export const getAttentionSeverityLevels = (
  items: { severity: TAttentionStatusSeverity }[]
): TAttentionStatusSeverity[] =>
  [...new Set(items.map(({ severity }) => severity))].sort((left, right) => right - left)
